import React from 'react';
import { Button } from 'src/ui/Button';
import { Spinner } from 'src/ui/Spinner';
import { useToast } from 'src/ui/Toast';
import {
  useGetPoolSnapshotQuery,
  useGeneratePoolSnapshotMutation,
  PoolBucketSummary,
} from 'src/store/vectorProfileApi';
import { Histogram } from 'src/ui/charts/Histogram';
import { CategoryBars, CategoryRow } from 'src/ui/charts/CategoryBars';

const card: React.CSSProperties = {
  background: 'var(--bg-canvas)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: 20,
};

const SubLabel = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 'var(--fs-2xs)',
      fontWeight: 600,
      letterSpacing: '.02em',
      textTransform: 'uppercase',
      color: 'var(--text-faint)',
      margin: '2px 0 8px',
    }}
  >
    {children}
  </div>
);

const toRows = (
  items: { value?: string; city?: string; count: number }[]
): CategoryRow[] =>
  items.map((r) => ({ label: r.value ?? r.city ?? '—', count: r.count }));

function PoolCard({ pool }: { pool: PoolBucketSummary }) {
  const a = pool.aggregates;
  return (
    <div style={card}>
      <div
        className="row"
        style={{
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 14,
        }}
      >
        <span
          style={{
            fontSize: 'var(--fs-sm)',
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          {pool.label}
        </span>
        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
          {a.poolSize.toLocaleString('en-US')} candidates
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Histogram
          title="Age"
          emphasizeAll
          bins={a.ageBins.map((b) => ({ ...b, inRange: false }))}
          unit="yrs"
        />
        <Histogram
          title="Height"
          emphasizeAll
          bins={a.heightBins.map((b) => ({ ...b, inRange: false }))}
          unit="cm"
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        <div>
          <SubLabel>Religion</SubLabel>
          <CategoryBars rows={toRows(a.religionCounts)} limit={6} />
        </div>
        <div>
          <SubLabel>Smoking</SubLabel>
          <CategoryBars rows={toRows(a.smokingCounts)} />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <SubLabel>Top metros</SubLabel>
        <CategoryBars rows={toRows(a.topMetros)} limit={8} />
      </div>
    </div>
  );
}

/**
 * Admin "Candidate Pool Health" — a one-time, gender-separated snapshot of the
 * pool's fact distributions (supply side), generated on demand. The watermark
 * shows when it was last generated. Per-user "Match Reach" (on the user profile)
 * reads the same snapshot.
 */
export function PoolHealth() {
  const push = useToast();
  const { data, isLoading } = useGetPoolSnapshotQuery();
  const [generate, { isLoading: generating }] =
    useGeneratePoolSnapshotMutation();

  const onGenerate = async () => {
    try {
      const res = await generate().unwrap();
      const total = res.pools.reduce((s, p) => s + p.aggregates.poolSize, 0);
      push({
        title: 'Pool analysis generated',
        message: `${total.toLocaleString('en-US')} candidates across ${
          res.pools.length
        } pools.`,
        type: 'success',
      });
    } catch (e: any) {
      push({
        title: 'Generation failed',
        message: e?.data?.message || 'Could not generate the pool snapshot.',
        type: 'error',
      });
    }
  };

  const hasSnapshot = data && data.hasSnapshot;

  return (
    <div style={{ ...card, padding: 18 }}>
      <div
        className="row"
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 'var(--fs-sm)',
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          Candidate Pool Health
        </span>
        <div className="row" style={{ gap: 12, alignItems: 'center' }}>
          {hasSnapshot && (
            <span
              style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-faint)' }}
            >
              generated {new Date(data.generatedAt).toLocaleString()}
            </span>
          )}
          <Button
            size="sm"
            variant="primary"
            onClick={onGenerate}
            disabled={generating}
          >
            {generating
              ? 'Generating…'
              : hasSnapshot
              ? 'Regenerate'
              : 'Generate pool analysis'}
          </Button>
        </div>
      </div>
      <div
        style={{
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-secondary)',
          marginBottom: 16,
        }}
      >
        Supply-side fact distribution of the matchable candidate pool, by
        gender.
      </div>

      {isLoading ? (
        <Spinner size="sm" />
      ) : !hasSnapshot ? (
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-faint)' }}>
          No snapshot yet — click “Generate pool analysis” to build one.
        </span>
      ) : (
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
        >
          {data.pools.map((p) => (
            <PoolCard key={p.gender} pool={p} />
          ))}
        </div>
      )}
    </div>
  );
}
